import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  draft:    { label: 'Taslak',   color: '#64748b', bg: '#f1f5f9' },
  sent:     { label: 'Gönderildi', color: '#f59e0b', bg: '#fffbeb' },
  viewed:   { label: 'Görüntülendi', color: '#6366f1', bg: '#eef2ff' },
  accepted: { label: 'Kabul Edildi', color: '#10b981', bg: '#ecfdf5' },
  rejected: { label: 'Reddedildi',  color: '#ef4444', bg: '#fff5f5' },
  expired:  { label: 'Süresi Doldu', color: '#94a3b8', bg: '#f8fafc' },
  revised:  { label: 'Revize',    color: '#8b5cf6', bg: '#f5f3ff' },
};

const CABIN_TYPES = ['Pivot Kapı', 'Sürgülü Kapı', 'Köşe Kabin', 'Yarım Daire Kabin', 'Walk-In Duş', 'Asimetrik Kabin', 'Özel Ölçü'];
const GLASS_TYPES = ['Şeffaf', 'Buzlu', 'Model Cam', 'Bronz', 'Gri', 'Siyah'];
const PROFILE_COLORS = ['Krom', 'Mat Krom', 'Siyah Mat', 'Altın', 'Bronz', 'Beyaz'];
const CITIES = ['Adana','Ankara','Antalya','Bursa','Eskişehir','Gaziantep','İstanbul','İzmir','Kayseri','Konya','Mersin','Samsun','Trabzon','Diğer'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const api = (path, opts = {}) =>
  axios({ url: `/api${path}`, headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }, ...opts });

const money = n => n != null ? Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 0 }) + ' ₺' : '-';
const fmt = d => d ? new Date(d).toLocaleDateString('tr-TR') : '-';
const emptyItem = () => ({ _k: Date.now() + Math.random(), type: 'Pivot Kapı', width: '', height: '', glass_type: 'Şeffaf', profile_color: 'Krom', quantity: 1, unit_price: '', description: '' });
const emptyForm = () => ({
  first_name: '', last_name: '', phone: '', city: '',
  items: [emptyItem()],
  discount_amount: 0, vat_rate: 20, installation_fee: 0,
  valid_until: '', notes: '',
});

// ─── Quote Form Modal ─────────────────────────────────────────────────────────
function QuoteModal({ onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const subtotal = form.items.reduce((s, it) => s + (Number(it.unit_price) * Number(it.quantity || 1)), 0);
  const vatAmount = Math.round((subtotal - Number(form.discount_amount)) * Number(form.vat_rate) / 100);
  const total = subtotal - Number(form.discount_amount) + vatAmount + Number(form.installation_fee);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem = (k, i, v) => setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; });
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = i => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const submit = async (status) => {
    if (!form.first_name || !form.phone) return setError('Ad ve telefon zorunludur.');
    if (form.items.some(it => !it.width || !it.height || !it.unit_price))
      return setError('Tüm kalemlerde genişlik, yükseklik ve fiyat giriniz.');
    setSaving(true); setError('');
    try {
      await api('/quotes/direct', {
        method: 'post',
        data: {
          first_name: form.first_name, last_name: form.last_name, phone: form.phone, city: form.city,
          subtotal, discount_amount: Number(form.discount_amount),
          vat_amount: vatAmount, vat_rate: Number(form.vat_rate),
          installation_fee: Number(form.installation_fee), total_amount: total,
          valid_until: form.valid_until || null,
          notes: form.notes,
          status,
          items: form.items.map(({ _k, ...r }) => r),
        },
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Kayıt hatası');
    } finally { setSaving(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...mBox, width: '780px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>📄 Yeni Teklif</h2>
          <button onClick={onClose} style={xBtn}>×</button>
        </div>
        {error && <div style={errBox}>{error}</div>}

        <Sec title="👤 Müşteri">
          <div style={g2}>
            <F l="Ad *"><input style={inp} value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Ad" /></F>
            <F l="Soyad"><input style={inp} value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Soyad" /></F>
            <F l="Telefon *"><input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="05xx xxx xx xx" /></F>
            <F l="Şehir">
              <select style={inp} value={form.city} onChange={e => set('city', e.target.value)}>
                <option value="">Seçin</option>
                {CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
          </div>
        </Sec>

        <Sec title="📐 Ürün Kalemleri">
          {form.items.map((item, idx) => (
            <div key={item._k} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--tertiary)' }}>Kalem {idx + 1}</span>
                {form.items.length > 1 && (
                  <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
                <F l="Kabin Tipi"><select style={inp} value={item.type} onChange={e => setItem('type', idx, e.target.value)}>{CABIN_TYPES.map(t => <option key={t}>{t}</option>)}</select></F>
                <F l="Genişlik (cm)"><input style={inp} type="number" value={item.width} onChange={e => setItem('width', idx, e.target.value)} placeholder="örn: 90" /></F>
                <F l="Yükseklik (cm)"><input style={inp} type="number" value={item.height} onChange={e => setItem('height', idx, e.target.value)} placeholder="örn: 200" /></F>
                <F l="Cam"><select style={inp} value={item.glass_type} onChange={e => setItem('glass_type', idx, e.target.value)}>{GLASS_TYPES.map(g => <option key={g}>{g}</option>)}</select></F>
                <F l="Profil Rengi"><select style={inp} value={item.profile_color} onChange={e => setItem('profile_color', idx, e.target.value)}>{PROFILE_COLORS.map(p => <option key={p}>{p}</option>)}</select></F>
                <F l="Adet"><input style={inp} type="number" min="1" value={item.quantity} onChange={e => setItem('quantity', idx, e.target.value)} /></F>
                <F l="Birim Fiyat (₺)"><input style={inp} type="number" value={item.unit_price} onChange={e => setItem('unit_price', idx, e.target.value)} placeholder="0" /></F>
                <F l="Satır Toplamı"><input style={{ ...inp, background: '#f1f5f9', color: '#475569' }} readOnly value={item.unit_price ? (Number(item.unit_price) * Number(item.quantity || 1)).toLocaleString('tr-TR') + ' ₺' : '-'} /></F>
              </div>
              <div style={{ marginTop: '10px' }}>
                <F l="Açıklama / Not"><input style={inp} value={item.description} onChange={e => setItem('description', idx, e.target.value)} placeholder="Opsiyonel kalem açıklaması" /></F>
              </div>
            </div>
          ))}
          <button onClick={addItem} style={{ ...btnOut, width: '100%' }}>+ Kalem Ekle</button>
        </Sec>

        <Sec title="💰 Fiyatlandırma">
          <div style={g3}>
            <F l="Ara Toplam"><input style={{ ...inp, background: '#f1f5f9', color: '#475569' }} readOnly value={subtotal.toLocaleString('tr-TR') + ' ₺'} /></F>
            <F l="İndirim (₺)"><input style={inp} type="number" value={form.discount_amount} onChange={e => set('discount_amount', e.target.value)} /></F>
            <F l="KDV (%)"><input style={inp} type="number" value={form.vat_rate} onChange={e => set('vat_rate', e.target.value)} /></F>
            <F l="KDV Tutarı"><input style={{ ...inp, background: '#f1f5f9', color: '#475569' }} readOnly value={vatAmount.toLocaleString('tr-TR') + ' ₺'} /></F>
            <F l="Montaj Ücreti (₺)"><input style={inp} type="number" value={form.installation_fee} onChange={e => set('installation_fee', e.target.value)} /></F>
            <F l="GENEL TOPLAM"><input style={{ ...inp, background: '#ecfdf5', color: '#059669', fontWeight: 800, fontSize: '15px' }} readOnly value={total.toLocaleString('tr-TR') + ' ₺'} /></F>
          </div>
        </Sec>

        <Sec title="📅 Geçerlilik & Notlar">
          <div style={g2}>
            <F l="Geçerlilik Tarihi"><input style={inp} type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} /></F>
            <F l="Notlar"><input style={inp} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Özel not..." /></F>
          </div>
        </Sec>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSec}>İptal</button>
          <button onClick={() => realSubmit('draft')} disabled={saving} style={{ ...btnSec, borderColor: 'var(--tertiary)', color: 'var(--tertiary)' }}>Taslak Kaydet</button>
          <button onClick={() => realSubmit('sent')} disabled={saving} style={{ ...btnPri, opacity: saving ? .7 : 1 }}>
            {saving ? 'Kaydediliyor…' : '📤 Teklif Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ quote, onClose, onUpdated }) {
  const [status, setStatus] = useState(quote.status);
  const [saving, setSaving] = useState(false);
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  const items = (() => { try { return JSON.parse(quote.items || '[]'); } catch { return []; } })();

  const downloadPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      await import('jspdf-autotable');
      
      const doc = new jsPDF();
      
      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(15, 76, 129); // Primary color
      doc.text("RITSA CRM TEKLIF FORMU", 20, 25);
      
      // Divider
      doc.setDrawColor(226, 232, 240);
      doc.line(20, 30, 190, 30);
      
      // Metadata
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(`Teklif No: ${quote.quote_number}`, 20, 40);
      doc.text(`Tarih: ${new Date(quote.created_at).toLocaleDateString('tr-TR')}`, 20, 46);
      doc.text(`Gecerlilik: ${quote.valid_until ? new Date(quote.valid_until).toLocaleDateString('tr-TR') : '-'}`, 20, 52);
      
      doc.text(`Musteri: ${quote.customer_first_name} ${quote.customer_last_name}`, 120, 40);
      doc.text(`Telefon: ${quote.customer_phone || '-'}`, 120, 46);
      doc.text(`Sehir: ${quote.customer_city || '-'}`, 120, 52);
      
      doc.line(20, 58, 190, 58);
      
      // Items Table
      const tableRows = items.map((it, idx) => [
        idx + 1,
        it.type,
        `${it.width}x${it.height}`,
        it.glass_type,
        it.profile_color,
        it.quantity,
        `${Number(it.unit_price).toLocaleString('tr-TR')} TL`,
        `${(Number(it.unit_price) * Number(it.quantity)).toLocaleString('tr-TR')} TL`
      ]);
      
      doc.autoTable({
        startY: 64,
        head: [['#', 'Tip', 'Olcu', 'Cam', 'Profil', 'Adet', 'Birim Fiyat', 'Toplam']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [15, 76, 129] },
        styles: { fontSize: 9 }
      });
      
      // Totals
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFont("helvetica", "bold");
      doc.text(`Ara Toplam: ${Number(quote.subtotal).toLocaleString('tr-TR')} TL`, 120, finalY);
      if (quote.discount_amount > 0) {
        doc.text(`Indirim: -${Number(quote.discount_amount).toLocaleString('tr-TR')} TL`, 120, finalY + 6);
      }
      doc.text(`KDV (%${quote.vat_rate || 20}): ${Number(quote.vat_amount).toLocaleString('tr-TR')} TL`, 120, finalY + 12);
      if (quote.installation_fee > 0) {
        doc.text(`Montaj: ${Number(quote.installation_fee).toLocaleString('tr-TR')} TL`, 120, finalY + 18);
      }
      
      doc.setFontSize(12);
      doc.setTextColor(5, 150, 105);
      doc.text(`GENEL TOPLAM: ${Number(quote.total_amount).toLocaleString('tr-TR')} TL`, 120, finalY + 26);
      
      if (quote.notes) {
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        doc.setFont("helvetica", "normal");
        doc.text("Notlar:", 20, finalY + 38);
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(quote.notes, 20, finalY + 44);
      }
      
      doc.save(`teklif-${quote.quote_number}.pdf`);
    } catch (e) {
      console.error(e);
      alert('PDF Olusturulamadi.');
    }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `Merhaba ${quote.customer_first_name} ${quote.customer_last_name}, \n` +
      `Sizin için hazırladığımız ${quote.quote_number} numaralı fiyat teklifi:\n` +
      `- Ara Tutar: ${Number(quote.subtotal).toLocaleString('tr-TR')} TL\n` +
      `- KDV: ${Number(quote.vat_amount).toLocaleString('tr-TR')} TL\n` +
      `- Genel Toplam: ${Number(quote.total_amount).toLocaleString('tr-TR')} TL\n\n` +
      `Detayları görüşmek üzere dönüşünüzü bekliyoruz.`
    );
    window.open(`https://api.whatsapp.com/send?phone=${quote.customer_phone}&text=${text}`, '_blank');
  };

  const update = async (newStatus) => {
    setSaving(true);
    try {
      await api(`/quotes/${quote.id}`, { method: 'put', data: { status: newStatus } });
      setStatus(newStatus);
      onUpdated(quote.id, newStatus);
    } catch { alert('Güncellenemedi'); }
    setSaving(false);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...mBox, width: '620px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>{quote.quote_number}</span>
            <h2 style={{ margin: '4px 0 0', fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
              {quote.customer_first_name} {quote.customer_last_name}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={downloadPDF} style={{ ...btnSec, padding: '6px 12px', fontSize: '12px', height: '34px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              📥 PDF İndir
            </button>
            {quote.customer_phone && (
              <button onClick={shareWhatsApp} style={{ ...btnSec, borderColor: '#10b981', color: '#10b981', padding: '6px 12px', fontSize: '12px', height: '34px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                📱 Paylaş
              </button>
            )}
            <button onClick={onClose} style={xBtn}>×</button>
          </div>
        </div>

        {/* Status badge */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {Object.entries(STATUS_MAP).map(([key, val]) => (
            <button key={key} onClick={() => update(key)} disabled={saving}
              style={{ padding: '6px 14px', border: `2px solid ${status === key ? val.color : '#e2e8f0'}`,
                borderRadius: '20px', background: status === key ? val.bg : 'white',
                color: status === key ? val.color : '#94a3b8', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>
              {val.label}
            </button>
          ))}
        </div>

        {/* Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
          {[
            ['📞 Telefon', quote.customer_phone || '-'],
            ['📍 Şehir', quote.customer_city || '-'],
            ['💰 Toplam', money(quote.total_amount)],
            ['📅 Geçerlilik', fmt(quote.valid_until)],
            ['👤 Hazırlayan', [quote.prepared_by_first_name, quote.prepared_by_last_name].filter(Boolean).join(' ') || '-'],
            ['📅 Oluşturuldu', fmt(quote.created_at)],
          ].map(([l, v]) => (
            <div key={l} style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{l}</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginTop: '2px' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Fiyat detayı */}
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
          {[
            ['Ara Toplam', money(quote.subtotal)],
            ['İndirim', quote.discount_amount > 0 ? `-${money(quote.discount_amount)}` : '-'],
            [`KDV (%${quote.vat_rate || 20})`, money(quote.vat_amount)],
            ['Montaj', money(quote.installation_fee)],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
              <span>{l}</span><span>{v}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
            <span>TOPLAM</span><span>{money(quote.total_amount)}</span>
          </div>
        </div>

        {/* Items */}
        {items.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', marginBottom: '8px' }}>📐 ÜRÜN KALEMLERİ</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead><tr style={{ background: '#f8fafc' }}>
                  {['Tip', 'Ölçü', 'Cam', 'Profil', 'Adet', 'Birim', 'Toplam'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 10px', color: '#1e293b', fontWeight: 500 }}>{it.type}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.width}×{it.height}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.glass_type}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.profile_color}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{it.quantity}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{money(it.unit_price)}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0f172a' }}>{money(Number(it.unit_price) * Number(it.quantity || 1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {quote.notes && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', marginBottom: '4px' }}>NOT</div>
            <div style={{ fontSize: '13px', color: '#78350f' }}>{quote.notes}</div>
          </div>
        )}

        {/* Convert to order button */}
        {status === 'accepted' && (
          <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: '#065f46', marginBottom: '10px', fontWeight: 600 }}>
              ✅ Teklif onaylandı — Üretime almak ister misiniz?
            </div>
            <button style={{ ...btnPri, background: '#10b981', fontSize: '13px' }}
              onClick={async () => {
                try {
                  const items = (() => { try { return JSON.parse(quote.items || '[]'); } catch { return []; } })();
                  await api('/orders/direct', {
                    method: 'post',
                    data: {
                      first_name: quote.customer_first_name,
                      last_name: quote.customer_last_name,
                      phone: quote.customer_phone,
                      city: quote.customer_city,
                      items, total_amount: quote.total_amount,
                      subtotal: quote.subtotal,
                      discount_amount: quote.discount_amount,
                      vat_amount: quote.vat_amount,
                      installation_fee: quote.installation_fee,
                    },
                  });
                  alert('Üretim siparişi oluşturuldu!');
                  onClose();
                } catch { alert('Hata oluştu'); }
              }}>
              🏭 Üretime Al
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Quote Card ───────────────────────────────────────────────────────────────
function QuoteCard({ q, onClick }) {
  const s = STATUS_MAP[q.status] || STATUS_MAP.draft;
  return (
    <div onClick={onClick} style={{ background: 'white', borderRadius: '14px', padding: '18px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', cursor: 'pointer', border: '1px solid #f1f5f9', transition: 'all .15s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'none'; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>{q.quote_number}</span>
        <span style={{ fontSize: '11px', fontWeight: 700, color: s.color, background: s.bg, padding: '3px 10px', borderRadius: '20px' }}>{s.label}</span>
      </div>
      <div style={{ fontWeight: 800, fontSize: '16px', color: '#0f172a', marginBottom: '4px' }}>
        {q.customer_first_name} {q.customer_last_name}
      </div>
      {q.customer_phone && <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '2px' }}>📞 {q.customer_phone}</div>}
      {q.customer_city && <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '10px' }}>📍 {q.customer_city}</div>}
      <div style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginBottom: '6px' }}>{money(q.total_amount)}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8' }}>
        <span>📅 {fmt(q.created_at)}</span>
        {q.valid_until && <span>⏳ {fmt(q.valid_until)}'e kadar</span>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  const fetchQuotes = useCallback(async () => {
    try { setQuotes((await api('/quotes')).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleUpdated = (id, status) => setQuotes(prev => prev.map(q => q.id === id ? { ...q, status } : q));

  const filtered = quotes.filter(q => {
    const ok = filterStatus === 'all' || q.status === filterStatus;
    const term = search.toLowerCase();
    const match = !term || [q.customer_first_name, q.customer_last_name, q.quote_number, q.customer_phone, q.customer_city].some(v => v?.toLowerCase().includes(term));
    return ok && match;
  });

  const totalValue = quotes.filter(q => q.status === 'accepted').reduce((s, q) => s + Number(q.total_amount || 0), 0);
  const pendingValue = quotes.filter(q => ['draft','sent','viewed'].includes(q.status)).reduce((s, q) => s + Number(q.total_amount || 0), 0);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px', color: 'var(--tertiary)', flexDirection: 'column' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e0e7ff', borderTopColor: 'var(--tertiary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <span style={{ fontWeight: 600 }}>Yükleniyor...</span>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc' }}>

      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #f1f5f9', padding: '18px 24px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>📄 Teklif Yönetimi</h1>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#94a3b8' }}>Müşteri tekliflerini oluşturun ve takip edin</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={fetchQuotes} style={btnSec}>🔄 Yenile</button>
            <button onClick={() => setShowNew(true)} style={btnPri}>+ Yeni Teklif</button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', overflowX: 'auto' }}>
          {[
            { l: 'Toplam', v: quotes.length, c: '#6366f1', b: '#eef2ff', i: '📋' },
            { l: 'Kabul Edilen', v: money(totalValue), c: '#10b981', b: '#ecfdf5', i: '✅' },
            { l: 'Bekleyen', v: money(pendingValue), c: '#f59e0b', b: '#fffbeb', i: '⏳' },
            ...Object.entries(STATUS_MAP).map(([k, v]) => ({ l: v.label, v: quotes.filter(q => q.status === k).length, c: v.color, b: v.bg, i: '' })),
          ].map(st => (
            <div key={st.l} style={{ minWidth: '110px', background: st.b, padding: '10px 14px', borderRadius: '10px', flexShrink: 0 }}>
              <div style={{ fontSize: '14px' }}>{st.i}</div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: st.c }}>{st.v}</div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>{st.l}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Müşteri, teklif no, telefon..."
            style={{ ...inp, width: '240px', padding: '8px 12px', fontSize: '13px' }} />
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <FilterBtn active={filterStatus === 'all'} color="#1e293b" onClick={() => setFilterStatus('all')}>Tümü ({quotes.length})</FilterBtn>
            {Object.entries(STATUS_MAP).map(([k, v]) => (
              <FilterBtn key={k} active={filterStatus === k} color={v.color} onClick={() => setFilterStatus(k)}>
                {v.label} ({quotes.filter(q => q.status === k).length})
              </FilterBtn>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 24px', color: '#94a3b8' }}>
            <div style={{ fontSize: '52px', marginBottom: '12px' }}>📄</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
              {quotes.length === 0 ? 'Henüz teklif yok' : 'Sonuç bulunamadı'}
            </div>
            {quotes.length === 0 && <div style={{ fontSize: '14px' }}>"Yeni Teklif" butonu ile başlayın.</div>}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
            {filtered.map(q => (
              <QuoteCard key={q.id} q={q} onClick={() => setSelected(q)} />
            ))}
          </div>
        )}
      </div>

      {showNew && <QuoteModal onClose={() => setShowNew(false)} onCreated={fetchQuotes} />}
      {selected && <DetailModal quote={{ ...selected, ...quotes.find(q => q.id === selected.id) }} onClose={() => setSelected(null)} onUpdated={handleUpdated} />}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Sec({ title, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '12px' }}>{title}</div>
      {children}
    </div>
  );
}
function F({ l, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>{l}</label>
      {children}
    </div>
  );
}
function FilterBtn({ active, color, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding: '6px 14px', borderRadius: '20px', border: `2px solid ${active ? color : '#e2e8f0'}`, background: active ? color : 'white', color: active ? 'white' : '#64748b', fontWeight: 600, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s' }}>
      {children}
    </button>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' };
const mBox    = { background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
const xBtn    = { background: 'none', border: 'none', fontSize: '26px', cursor: 'pointer', color: '#64748b', lineHeight: 1 };
const inp     = { border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#1e293b' };
const g2      = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' };
const g3      = { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' };
const btnPri  = { padding: '10px 22px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' };
const btnSec  = { padding: '10px 22px', background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' };
const btnOut  = { padding: '10px', background: 'white', color: '#6366f1', border: '1px dashed #a5b4fc', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const errBox  = { background: '#fff5f5', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '16px' };
